import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — please log in");
      navigate("/login", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "This reset link is invalid or has expired");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="reset-no-token">
        <div className="w-full max-w-md text-center">
          <h1 className="font-display text-3xl mb-2">Invalid reset link</h1>
          <p className="text-muted-foreground text-sm mb-8">
            This link is missing or malformed. Please request a new password reset.
          </p>
          <Link to="/forgot-password" className="text-[#C05A3C] font-medium hover:underline" data-testid="request-new-link">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-8" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="font-display text-2xl mb-8 inline-block">AfriMarket</Link>
        <h1 className="font-display text-3xl mb-2">Set a new password</h1>
        <p className="text-muted-foreground text-sm mb-8">Choose a strong password you don&apos;t use elsewhere.</p>
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div>
            <Label htmlFor="password" className="label-overline">New password</Label>
            <Input
              id="password"
              data-testid="reset-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              className="mt-2"
            />
          </div>
          <div>
            <Label htmlFor="confirm" className="label-overline">Confirm password</Label>
            <Input
              id="confirm"
              data-testid="reset-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              minLength={6}
              className="mt-2"
            />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-12 bg-[#C05A3C] hover:bg-[#D46B4E] rounded-md" data-testid="reset-submit">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
          </Button>
        </form>
        <p className="text-sm text-muted-foreground mt-6">
          <Link to="/login" className="text-[#C05A3C] font-medium hover:underline" data-testid="reset-link-login">← Back to log in</Link>
        </p>
      </div>
    </div>
  );
};

export default ResetPassword;
